import {
  CreateReviewerScreeningVote,
  ChangeReviewerScreeningVote,
  RemoveReviewerScreeningVote,
} from '../../graphql';
import { apolloClient } from '@/plugins/apollo';
import { ReviewerScreeningVote } from '@/models';
import {
  AssessmentState,
  RelevanceAssessment,
  ScreeningOutcomeResolution,
} from '@/enums';
import { v4 as createUUID } from 'uuid';
import {
  appendToListSubQueryCache,
  deleteFromListSubQueryCacheWhere,
} from '@/modules/cacheHelpers';
import {
  refreshScreeningCounts,
  refreshScreeningInsights,
} from '@/modules/screeningCountsRefresh';

const createOptimisticResponseForSaveVote = async (
  context,
  reviewerScreeningVote,
  isUpdate
) => {
  const optimisticId = isUpdate
    ? reviewerScreeningVote.id
    : createUUID();
  const screeningOutcomeOptimisticId = createUUID();

  const existingRef = reviewerScreeningVote.reference;
  let screeningState = existingRef.screeningState;
  let review = existingRef.review;
  let screeningOutcome = existingRef.screeningOutcome?.id
    ? existingRef.screeningOutcome
    : null;
  if (!isUpdate) {
    const requiredVotes = await context.dispatch(
      'review/fetchRequiredScreeningVotes',
      existingRef.review.id,
      { root: true }
    );
    screeningState =
      requiredVotes > 1
        ? AssessmentState.PARTIAL
        : AssessmentState.COMPLETE;
    screeningOutcome =
      requiredVotes > 1
        ? screeningOutcome
        : {
            id: screeningOutcomeOptimisticId,
            optimisticId: screeningOutcomeOptimisticId,
            outcome: reviewerScreeningVote.vote,
            resolution: ScreeningOutcomeResolution.UNANIMOUS_VOTE,
            resolvedBy: null,
            reference: {
              id: existingRef.id,
              __typename: 'Reference',
            },
            __typename: 'ScreeningOutcome',
          };

    // Optimistically update the review's training stats
    review.trainingStats.votesUntilTriggerTrainingCount = Math.max(
      0,
      review.trainingStats.votesUntilTriggerTrainingCount - 1
    );
    switch (reviewerScreeningVote.vote) {
      case RelevanceAssessment.RELEVANT:
        review.trainingStats.requiredRelevantVotesRemainingCount = Math.max(
          0,
          review.trainingStats.requiredRelevantVotesRemainingCount - 1
        );
        break;
      case RelevanceAssessment.NOT_RELEVANT:
        review.trainingStats.requiredNotRelevantVotesRemainingCount = Math.max(
          0,
          review.trainingStats
            .requiredNotRelevantVotesRemainingCount - 1
        );
        break;
    }
  }
  const key = isUpdate
    ? 'changeReviewerScreeningVote'
    : 'createReviewerScreeningVote';
  return {
    [key]: {
      ...reviewerScreeningVote,
      id: optimisticId,
      optimisticId,
      reference: {
        ...reviewerScreeningVote.reference,
        review,
        screeningState,
        screeningOutcome,
      },
      __typename: 'ReviewerScreeningVote',
    },
  };
};

const actions = {
  async saveAndAwaitRefetch(context, reviewerScreeningVote) {
    return context.dispatch('internalSave', {
      reviewerScreeningVote,
      awaitRefetch: true,
    });
  },
  async saveOptimistically(context, reviewerScreeningVote) {
    return context.dispatch('internalSave', {
      reviewerScreeningVote,
      optimistic: true,
    });
  },
  async save(context, reviewerScreeningVote) {
    return context.dispatch('internalSave', {
      reviewerScreeningVote,
    });
  },
  async internalSave(
    context,
    {
      reviewerScreeningVote,
      awaitRefetch = false,
      optimistic = false,
    }
  ) {
    const isUpdate =
      reviewerScreeningVote.id && !reviewerScreeningVote.optimisticId;
    const mutation = isUpdate
      ? ChangeReviewerScreeningVote
      : CreateReviewerScreeningVote;

    let optimisticResponse = undefined;
    if (optimistic) {
      optimisticResponse = await createOptimisticResponseForSaveVote(
        context,
        reviewerScreeningVote,
        isUpdate
      );
    }

    const refetchQueries = [
      'GetReference',
      'GetReferencesByReviewIdAndFilters',
      'GetReferencesByFilters',
      'SearchReferences',
      'GetRelevanceAssessmentCountsByReviewIdAndFilters',
      'GetScreeningOutcomesByReviewId',
      'GetTopReferencesForScreening',
    ];

    const response = await apolloClient.mutate({
      mutation,
      variables: {
        reviewerScreeningVote: reviewerScreeningVote.creationData(),
      },
      optimisticResponse,
      update(cache, { data }) {
        const referenceId = reviewerScreeningVote.reference.id;
        deleteFromListSubQueryCacheWhere(
          cache,
          referenceId,
          'Reference',
          'reviewerScreeningVotes',
          ({ item, readField }) =>
            readField('id', readField('reviewer', item)) ===
            reviewerScreeningVote.reviewer.id
        );
        appendToListSubQueryCache(
          cache,
          referenceId,
          'Reference',
          'reviewerScreeningVotes',
          data?.createReviewerScreeningVote ||
            data?.changeReviewerScreeningVote,
          0
        );
      },
      refetchQueries: optimistic ? [] : refetchQueries,
      awaitRefetchQueries: awaitRefetch,
    });

    const data = reviewerScreeningVote.id
      ? response.data.changeReviewerScreeningVote
      : response.data.createReviewerScreeningVote;
    refreshScreeningCounts(
      reviewerScreeningVote.reference?.review?.id
    );
    refreshScreeningInsights(
      reviewerScreeningVote.reference?.review?.id
    );
    return new ReviewerScreeningVote(data);
  },
  async delete(context, reviewerScreeningVote) {
    const response = await apolloClient.mutate({
      mutation: RemoveReviewerScreeningVote,
      variables: {
        id: reviewerScreeningVote?.id || reviewerScreeningVote,
      },
      refetchQueries: [
        'GetReference',
        'GetReferencesByReviewIdAndFilters',
        'GetReferencesByFilters',
        'SearchReferences',
        'GetRelevanceAssessmentCountsByReviewIdAndFilters',
        'GetScreeningOutcomesByReviewId',
      ],
      awaitRefetchQueries: true,
    });
    refreshScreeningCounts(
      reviewerScreeningVote?.reference?.review?.id
    );
    refreshScreeningInsights(
      reviewerScreeningVote?.reference?.review?.id
    );
    return response.data.removeReviewerScreeningVote;
  },
  async deleteWithCacheUpdate(
    context,
    { id, reference, cacheUpdate }
  ) {
    const response = await apolloClient.mutate({
      mutation: RemoveReviewerScreeningVote,
      variables: {
        id,
      },
      update(cache, { data }) {
        cacheUpdate(cache, data.removeReviewerScreeningVote);
      },
    });
    refreshScreeningCounts(reference?.review?.id);
    refreshScreeningInsights(reference?.review?.id);
    return new ReviewerScreeningVote(
      response.data.removeReviewerScreeningVote
    );
  },
};

export default {
  namespaced: true,
  actions,
};
